/**
 * RBAC 権限フック + PermissionProvider (steering 565 / design §6.2)
 *
 * ## 何を変えたか
 *
 * 旧実装は **静的な既定表をロール名で引いて**権限を決めていた。これは 2 つの点で成立しない。
 *
 *   1. 組織ごとの権限上書き (steering 431) を一切参照しないので、**サーバの判定とずれる**
 *   2. カスタムロールは表に無いため **全判定 false** になり、画面がほぼ空になる
 *
 * そこで **`GET /admin/users/me` の `access` を唯一の権限ソース**にする。取得状態は 3 つ:
 *
 * | 状態 | 画面の挙動 |
 * |---|---|
 * | 取得中 | **権限を要する UI を出さない**。既定表へ暫定フォールバックしない |
 * | 取得成功 | `access.permissions` / `access.dataScope` で判定 |
 * | **取得失敗** | **全 deny** として扱い、再試行導線を出す (fail-safe) |
 *
 * **静的な既定表を権限昇格のフォールバックに使わない**のが不変条件である。
 * 既定表 (`utils/rolePermissionMatrix`) は「サーバ正本との差分検出テスト」用に残し、
 * 実行時の判定からは切り離した。
 *
 * ロール一覧 API は使わない — あれは管理画面の編集用で `rbacSettings:read` を要求するため、
 * 一般利用者は読めない。権限判定の依存先にしてはならない。
 */
import { createContext, useContext, useMemo, type ReactNode } from 'react';
import type {
  DataScope,
  MyRoleAccess,
  PermissionArea,
  PermissionAction,
  PermissionEntry,
  UserRole,
} from '@/types';
import { useAuth } from '@/hooks/useAuth';
import { useCurrentUserProfile } from '@/hooks/useAdmin';

// ==============================
// Context 定義
// ==============================

/** 実効権限の取得状態。**取得中と取得失敗を混同しない** (前者は待ち、後者は再試行導線)。 */
export type PermissionStatus = 'loading' | 'ready' | 'error';

interface PermissionContextValue {
  /** 指定エリアの読み取り権限を持つか */
  canRead(area: PermissionArea): boolean;
  /** 指定エリアの書き込み権限を持つか */
  canWrite(area: PermissionArea): boolean;
  /** 指定エリアの実行権限を持つか */
  canExecute(area: PermissionArea): boolean;
  /** 現在のユーザーロール ID (未認証なら null)。**判定には使わない** — 表示用 */
  role: UserRole | null;
  /** 実効アクセスの取得状態 */
  status: PermissionStatus;
  /** 閲覧できる対象範囲 (ロール単位)。取得できていなければ安全側の `assigned` */
  dataScope: DataScope;
  /**
   * permission-area-data-scope (ADR-107): 指定項目での閲覧範囲。項目に上書きが無ければ `dataScope`。
   * 取得できていなければ安全側の `assigned`。サーバ (`effectiveDataScope`) と同じ規則。
   */
  dataScopeFor(area: PermissionArea): DataScope;
  /** 取得できた実効アクセス (未取得なら null) */
  access: MyRoleAccess | null;
  /** 取得失敗時の再試行 */
  retry(): void;
}

const PermissionContext = createContext<PermissionContextValue | null>(null);

/** 全 deny の 1 エリア分。 */
const DENY: PermissionEntry = { read: false, write: false, execute: false };

// ==============================
// PermissionProvider
// ==============================

interface PermissionProviderProps {
  children: ReactNode;
}

/** 権限コンテキストプロバイダー */
export function PermissionProvider({ children }: PermissionProviderProps) {
  const { user } = useAuth();
  // 未認証では取得しない (401 を撃たない)。self-scoped endpoint なので追加権限は要らない。
  const { data, loading, error, refetch } = useCurrentUserProfile(!!user);

  const value = useMemo<PermissionContextValue>(() => {
    const role = user?.role ?? null;
    const access = data?.access ?? null;

    /**
     * 取得状態の決定。
     *
     * - 未認証 … `ready` (何も許可しない状態として確定している。待たせない)
     * - 取得中 … `loading`
     * - 失敗 / `access` 欠落 … `error` (**推測で埋めない**)
     * - `status !== 'ok'` … `ready` だが全 deny (サーバがそう決めている)
     */
    let status: PermissionStatus;
    if (!user) status = 'ready';
    else if (loading && !data) status = 'loading';
    else if (error !== null || access === null) status = 'error';
    else status = 'ready';

    const entryOf = (area: PermissionArea): PermissionEntry => {
      // 取得できていない / サーバが `ok` 以外と判定した / エリアが欠けている
      // — いずれも **全 deny**。既定表へ落ちない。
      if (status !== 'ready' || !access || access.status !== 'ok') return DENY;
      return access.permissions[area] ?? DENY;
    };

    const hasPermission = (area: PermissionArea, action: PermissionAction): boolean =>
      entryOf(area)[action] === true;

    const ready = status === 'ready' && access?.status === 'ok';
    const roleScope: DataScope = ready && access ? access.dataScope : 'assigned';
    const dataScopeFor = (area: PermissionArea): DataScope => {
      if (!ready || !access) return 'assigned';
      // 「担当のみ (担当未設定も見る)」は全件ではない (担当未設定の追加はサーバが閲覧のときだけ行う)
      const scope = access.areaDataScopes?.[area] ?? access.dataScope;
      return scope === 'all' ? 'all' : 'assigned';
    };

    return {
      canRead: (area: PermissionArea) => hasPermission(area, 'read'),
      canWrite: (area: PermissionArea) => hasPermission(area, 'write'),
      canExecute: (area: PermissionArea) => hasPermission(area, 'execute'),
      role,
      status,
      // 取得できていなければ安全側 (絞り込む側) に倒す。
      dataScope: roleScope,
      dataScopeFor,
      access,
      retry: refetch,
    };
  }, [user, data, loading, error, refetch]);

  return <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>;
}

// ==============================
// usePermission フック
// ==============================

/** 権限コンテキストから RBAC 判定関数を取得する */
export function usePermission(): PermissionContextValue {
  const ctx = useContext(PermissionContext);
  if (!ctx) {
    throw new Error('usePermission は PermissionProvider 内で使用してください');
  }
  return ctx;
}
